import { useQuery } from '@apollo/client';
import { translateBackend } from '@assets/i18n/config';
import type { WorkflowCompletion } from '@generated/graphql';
import { getLanguageDefaults } from '@onevesthq/ov-translations';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import utc from 'dayjs/plugin/utc';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { computeChanges } from './computeChanges';
import {
  FETCH_LOGS_QUERY,
  GET_TOTAL_LOGS_QUERY,
} from './workflowReview.queries';
import WorkflowReviewCard from './workflowReviewCard';
import { isVisibleFieldChange } from './workflowReviewChangeUtils';

dayjs.extend(advancedFormat);
dayjs.extend(utc);

export const WorkflowReview = ({
  options,
  onNext,
  workflowCompletion,
  userId,
}: {
  options: any;
  onNext: any;
  workflowCompletion: WorkflowCompletion;
  userId?: string;
}) => {
  const { t } = useTranslation(['workflowCompletions']);
  const { sendAnalytic } = useContext(AnalyticsContext);

  const { data: totalLogsData, refetch: refetchTotalLogs } = useQuery(
    GET_TOTAL_LOGS_QUERY,
    {
      variables: {
        input: {
          filter: {
            userId: userId,
            objectId: userId,
            workflowCompletionId: workflowCompletion?.id,
          },
        },
      },
      skip: !workflowCompletion?.id,
      fetchPolicy: 'no-cache',
    },
  );
  const totalLogs = totalLogsData?.fetchLogs?.totalCount ?? 0;

  const {
    data: logsData,
    loading: logsLoading,
    refetch: refetchLogsQuery,
  } = useQuery(FETCH_LOGS_QUERY, {
    variables: {
      input: {
        filter: {
          userId: userId,
          objectId: userId,
          workflowCompletionId: workflowCompletion?.id,
        },
        pagination: {
          sortDesc: true, //Get latest logs first
          perPage: totalLogs,
        },
      },
    },
    // Only query logs if we have a valid totalCount and workflowCompletion is present
    skip: !workflowCompletion?.id || !totalLogs,
    fetchPolicy: 'no-cache',
  });
  const logs = logsData?.fetchLogs.logs;

  // Refetch both queries in sequence: first get updated total count, then fetch all logs
  const refetchLogs = useCallback(async () => {
    const { data: updatedTotalData } = await refetchTotalLogs();
    const newTotalCount = updatedTotalData?.fetchLogs?.totalCount ?? 0;
    if (newTotalCount > 0) {
      await refetchLogsQuery({
        input: {
          filter: {
            userId: userId,
            objectId: userId,
            workflowCompletionId: workflowCompletion?.id,
          },
          pagination: {
            sortDesc: true,
            perPage: newTotalCount,
          },
        },
      });
    }
  }, [refetchTotalLogs, refetchLogsQuery, userId, workflowCompletion?.id]);

  const changes = computeChanges(logs, workflowCompletion);
  const hasVisibleChanges = Object.values(changes).some((stepChanges) =>
    Object.values(stepChanges?.changes ?? {}).some(isVisibleFieldChange),
  );

  const onSubmit = async () => {
    sendAnalytic(ovAnalyticsEvents.workflowsReviewGoalContinueButtonSelect, {
      workflowStepTitle: options?.title,
      workflowId: workflowCompletion?.workflow?.id,
      workflowName: workflowCompletion?.workflow?.name,
      activeWorkflowCompletionId: workflowCompletion?.id,
      objectId: workflowCompletion?.objectId,
      objectType: workflowCompletion?.objectType,
    });
    await onNext();
  };

  return (
    <WorkflowCompletionForm
      onSubmit={onSubmit}
      testId='workflow-review-form'
      submitButtonProps={{
        label: t('continue'),
        disabled: logsLoading,
        dataTestId: 'workflow-review-continue-button',
      }}
    >
      {options?.title && (
        <Typography variant='displayLarge' sx={{ mt: 1 }}>
          {translateBackend(options.title)}
        </Typography>
      )}
      {options?.subtitle && (
        <Typography variant='bodyLarge' component='div' sx={{ mb: 3 }}>
          <Box display='inline-block'>
            <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
              {translateBackend(options.subtitle)}
            </ReactMarkdown>
          </Box>
        </Typography>
      )}
      {!hasVisibleChanges && (
        <Box
          sx={{
            p: 2,
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          }}
        >
          <Typography variant='bodyMedium'>
            {t('noChanges', { defaultValue: 'No changes' })}
          </Typography>
        </Box>
      )}
      {Object.keys(changes)
        .sort((a, b) => {
          const steps = workflowCompletion?.workflow?.steps ?? [];
          const indexA = steps.findIndex((s: any) => s?.id === a);
          const indexB = steps.findIndex((s: any) => s?.id === b);
          return (
            (indexA === -1 ? Infinity : indexA) -
            (indexB === -1 ? Infinity : indexB)
          );
        })
        .map((stepId) => {
          const stepChanges = changes[stepId];
          if (!stepChanges) return null;
          const hasVisibleChangesInStep = Object.values(
            stepChanges.changes,
          ).some(isVisibleFieldChange);
          if (!hasVisibleChangesInStep) return null;

          return (
            <WorkflowReviewCard
              key={stepId}
              changes={{ [stepId]: stepChanges }}
              stepName={stepChanges?.stepName ?? getLanguageDefaults()}
              firstSubStepIdOfStep={
                workflowCompletion?.workflow?.steps.find(
                  (step: any) => step?.id === stepId,
                )?.subSteps[0]?.id
              }
              workflowCompletion={workflowCompletion}
              stepId={stepId}
              refetchLogs={refetchLogs}
              enableEdit={options.enableEditChanges}
            />
          );
        })}
    </WorkflowCompletionForm>
  );
};

export default WorkflowReview;
